import { useState, useMemo, useEffect, useCallback } from 'react';
import { getMisafirUlkeIstatistik } from '../../services/api';
import YilDropdown from '../../components/YilDropdown';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import DataTable, { Rozet, OranCubugu } from '../../components/DataTable';

// ── Sabitler ──────────────────────────────────────────────────────────────
const AYLAR_KSA = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];
const AYLAR_TAM = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

// Bayrak emoji yardımcısı
const BAYRAKLAR = {
  'TÜRKİYE':      '🇹🇷',
  'TURKİYE':      '🇹🇷',
  'TURKEY':       '🇹🇷',
  'ALMANYA':      '🇩🇪',
  'GERMANY':      '🇩🇪',
  'RUSYA':        '🇷🇺',
  'RUSSIA':       '🇷🇺',
  'İNGİLTERE':   '🇬🇧',
  'UK':           '🇬🇧',
  'ENGLAND':      '🇬🇧',
  'FRANSA':       '🇫🇷',
  'FRANCE':       '🇫🇷',
  'HOLLANDA':     '🇳🇱',
  'NETHERLANDS':  '🇳🇱',
  'BELÇİKA':     '🇧🇪',
  'BELGIUM':      '🇧🇪',
  'İTALYA':      '🇮🇹',
  'ITALY':        '🇮🇹',
  'AVUSTURYA':   '🇦🇹',
  'AUSTRIA':      '🇦🇹',
  'İSPANYA':     '🇪🇸',
  'SPAIN':        '🇪🇸',
  'AMERİKA':     '🇺🇸',
  'USA':          '🇺🇸',
  'ABD':          '🇺🇸',
  'ÇİN':         '🇨🇳',
  'CHINA':        '🇨🇳',
  'JAPONYA':      '🇯🇵',
  'JAPAN':        '🇯🇵',
  'SUUD':         '🇸🇦',
  'ARABİSTAN':   '🇸🇦',
  'BİRLEŞİK':   '🇦🇪',
  'BAE':          '🇦🇪',
  'BURKINA FASO': '🇧🇫',
};

function bayrak(ulke) {
  if (!ulke) return '🌍';
  var ust = ulke.toUpperCase();
  // Tam eşleşme
  if (BAYRAKLAR[ust]) return BAYRAKLAR[ust];
  // Kısmi eşleşme
  var bulunan = Object.keys(BAYRAKLAR).find(function(k) { return ust.includes(k) || k.includes(ust); });
  return bulunan ? BAYRAKLAR[bulunan] : '🌍';
}

export default function MisafirUlke() {
  var now = new Date();
  var [yil,     setYil]     = useState(now.getFullYear());
  var [ay,      setAy]      = useState(now.getMonth()); // 0-indexed

  var [tumListe, setTumListe] = useState([]);
  var [loading,  setLoading]  = useState(false);
  var [error,    setError]    = useState(null);

  var fetchData = useCallback(function(y, m) {
    setLoading(true);
    setError(null);
    getMisafirUlkeIstatistik(y, m + 1) // ay 1-indexed gönder
      .then(function(data) {
        setTumListe(Array.isArray(data) ? data : []);
      })
      .catch(function() {
        setError('Veri alınamadı.');
        setTumListe([]);
      })
      .finally(function() {
        setLoading(false);
      });
  }, []);

  useEffect(function() {
    fetchData(yil, ay);
  }, [yil, ay, fetchData]);

  // Varsayılan sıra: en çok giriş yapan ülke başta. Kullanıcı sıralaması ve
  // filtresi artık DataTable içinde, sayfada ayrı bir mekanizma tutulmuyor.
  var liste = useMemo(function() {
    return tumListe.slice().sort(function(a, b) {
      return (b.girisSayisi || 0) - (a.girisSayisi || 0);
    });
  }, [tumListe]);

  var toplamGiris    = liste.reduce(function(s, d) { return s + (d.girisSayisi || 0); }, 0);
  var toplamGeceleme = liste.reduce(function(s, d) { return s + (d.gecelemeSayisi || 0); }, 0);
  var maxGiris       = liste.length > 0 ? Math.max.apply(null, liste.map(function(d) { return d.girisSayisi || 0; })) : 1;

  function handleExcel() {
    var baslik = ['Ülke', 'Giriş Sayısı', 'Geceleme Sayısı', 'Ort. Geceleme', 'Geceleme %'];
    var satirlar = liste.map(function(d) {
      var ort     = d.girisSayisi > 0 ? (d.gecelemeSayisi / d.girisSayisi).toFixed(1) : '0';
      var gecePct = toplamGeceleme > 0 ? ((d.gecelemeSayisi / toplamGeceleme) * 100).toFixed(1) : '0';
      return [d.ulke, d.girisSayisi, d.gecelemeSayisi, ort, gecePct + '%'];
    });
    downloadExcel('misafir-ulke-' + yil + '-' + String(ay + 1).padStart(2, '0'), 'Ülke Dağılımı', baslik, satirlar);
  }

  function handleYazdir() {
    var baslik = ['Ülke', 'Giriş Sayısı', 'Geceleme Sayısı', 'Ort. Geceleme', 'Geceleme %'];
    var satirlar = liste.map(function(d) {
      var ort     = d.girisSayisi > 0 ? (d.gecelemeSayisi / d.girisSayisi).toFixed(1) : '0';
      var gecePct = toplamGeceleme > 0 ? ((d.gecelemeSayisi / toplamGeceleme) * 100).toFixed(1) : '0';
      return [d.ulke, d.girisSayisi, d.gecelemeSayisi, ort, gecePct + '%'];
    });
    printTable('Misafir Ülke Dağılımı', baslik, satirlar, AYLAR_TAM[ay] + ' ' + yil + ' — ' + liste.length + ' ülke');
  }

  return (
    <div className="p-3 md:p-6 space-y-8 min-h-screen bg-gray-50/50" style={{ animation: 'fadeIn 0.3s ease-out' }}>

      <PageHeader
        icon="🌍"
        iconColor="from-blue-500 to-indigo-700"
        iconShadow="shadow-blue-200"
        title="Misafirler — Ülke Dağılımı"
        subtitle={`${AYLAR_TAM[ay]} ${yil} — Hedef Kitle Raporu`}
      >
        <div className="flex flex-wrap gap-4 items-center">
          {/* Hızlı Seçim */}
          <div className="flex bg-gray-50/80 p-2 rounded-2xl border border-gray-100 mr-2 z-10 hidden sm:flex">
            <YilDropdown value={yil} onChange={setYil} />
            <div className="w-px h-8 bg-gray-200 mx-2 self-center"></div>
            <select value={ay} onChange={e => setAy(Number(e.target.value))} className="bg-transparent font-black text-sm text-gray-700 focus:outline-none cursor-pointer pr-4">
              {AYLAR_TAM.map((a, i) => <option key={a} value={i}>{a}</option>)}
            </select>
          </div>
          <button onClick={handleExcel} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white border-2 border-emerald-100 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 font-black px-5 py-3 rounded-2xl shadow-lg shadow-emerald-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap">
            <span>📊</span> EXCEL
          </button>
          <button onClick={handleYazdir} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white border-2 border-gray-100 hover:border-gray-800 hover:bg-gray-800 hover:text-white text-gray-700 font-black px-5 py-3 rounded-2xl shadow-lg shadow-gray-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap">
            <span>🖨️</span> YAZDIR
          </button>
        </div>
      </PageHeader>

      {/* Mobil Hızlı Seçim (Sadece Dar Ekranda Görünür) */}
      <div className="sm:hidden bg-white rounded-[2.5rem] border border-gray-100 shadow-xl p-5 flex justify-center z-10 relative gap-4">
          <YilDropdown value={yil} onChange={setYil} />
          <select value={ay} onChange={e => setAy(Number(e.target.value))} className="bg-gray-50 border border-gray-200 rounded-xl px-4 font-black text-sm text-gray-700 focus:outline-none cursor-pointer">
            {AYLAR_TAM.map((a, i) => <option key={a} value={i}>{a}</option>)}
          </select>
      </div>

      {/* Hata */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-3 text-sm font-medium">
          {error}
        </div>
      )}

      {/* Özet kartlar (Premium) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-5 z-10 relative">
        <div className="relative overflow-hidden rounded-[2rem] p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group bg-gradient-to-br from-blue-50 to-indigo-50 border border-white shadow-lg flex flex-col justify-between">
          <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 group-hover:-rotate-12 transition-transform duration-500">🌍</div>
          <div className="flex items-start justify-between mb-2">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">Farklı Ülke Toplamı</span>
            <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white shadow-sm font-emoji">🌍</span>
          </div>
          <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter leading-none">{loading ? '…' : liste.length}</div>
          <p className="text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 text-blue-600">Hedef Ülke Kaydı</p>
        </div>

        <div className="relative overflow-hidden rounded-[2rem] p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group bg-gradient-to-br from-emerald-50 to-green-50 border border-white shadow-lg flex flex-col justify-between">
          <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 group-hover:-rotate-12 transition-transform duration-500">👥</div>
          <div className="flex items-start justify-between mb-2">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">Toplam Giriş</span>
            <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white shadow-sm font-emoji">👥</span>
          </div>
          <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter leading-none">{loading ? '…' : toplamGiris}</div>
          <p className="text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 text-emerald-600">Kişi</p>
        </div>

        <div className="relative overflow-hidden rounded-[2rem] p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-white shadow-lg flex flex-col justify-between">
          <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 group-hover:-rotate-12 transition-transform duration-500">🌙</div>
          <div className="flex items-start justify-between mb-2">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-purple-600">Toplam Geceleme</span>
            <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white shadow-sm font-emoji">🌙</span>
          </div>
          <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter leading-none">{loading ? '…' : toplamGeceleme}</div>
          <p className="text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 text-purple-600">Gece</p>
        </div>

        <div className="relative overflow-hidden rounded-[2rem] p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group bg-gradient-to-br from-orange-50 to-amber-50 border border-white shadow-lg flex flex-col justify-between">
          <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 group-hover:-rotate-12 transition-transform duration-500">📊</div>
          <div className="flex items-start justify-between mb-2">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-600">Ort. Geceleme</span>
            <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white shadow-sm font-emoji">📊</span>
          </div>
          <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter leading-none">{loading ? '…' : (toplamGiris > 0 ? (toplamGeceleme / toplamGiris).toFixed(1) : '0')}</div>
          <p className="text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 text-orange-600">Gece / Kişi</p>
        </div>
      </div>

      {/* Görsel dağılım */}
      {!loading && liste.length > 0 && (
        <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl p-8 z-10 relative">
          <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6">
            Pazar Payı Liderleri — Top 15 Dağılım ({AYLAR_TAM[ay]} {yil})
          </h3>
          <div className="space-y-4">
            {liste.slice(0, 15).map(function(d) {
              var pct      = maxGiris > 0 ? Math.round((d.girisSayisi / maxGiris) * 100) : 0;
              var gecelePct = toplamGeceleme > 0 ? ((d.gecelemeSayisi / toplamGeceleme) * 100).toFixed(1) : '0';
              return (
                <div key={d.ulke} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 group p-2 hover:bg-gray-50 rounded-2xl transition-all">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-2xl w-8 text-center flex-shrink-0 opacity-80 group-hover:scale-125 group-hover:opacity-100 transition-all">{bayrak(d.ulke)}</span>
                    <span className="text-[11px] font-black text-gray-800 uppercase tracking-widest w-24 sm:w-32 flex-shrink-0 truncate">{d.ulke}</span>
                    <div className="flex-1 h-6 bg-gray-50 rounded-full overflow-hidden border border-gray-100 shadow-inner p-0.5">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-400 to-indigo-600 flex items-center justify-end pr-3 transition-all duration-[1500ms]"
                        style={{ width: pct + '%', minWidth: '32px' }}>
                        <span className="text-white text-[10px] font-black leading-none drop-shadow-md">{d.girisSayisi}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between sm:justify-end items-center sm:w-24 flex-shrink-0 px-2 sm:px-0">
                    <span className="sm:hidden text-[9px] font-black text-gray-300 uppercase tracking-widest">Geceleme Payı</span>
                    <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-lg tracking-widest">{gecelePct}% GECE</span>
                  </div>
                </div>
              );
            })}
            {liste.length > 15 && (
              <p className="text-[10px] font-black uppercase text-gray-300 text-center pt-4 tracking-widest">+ {liste.length - 15} ülke daha mevcut</p>
            )}
          </div>
        </div>
      )}

      {/* Yükleniyor / Boş durum */}
      {loading && (
        <LoadingSpinner fullPage text="Yükleniyor..." />
      )}

      {!loading && tumListe.length === 0 && (
        <EmptyState icon="🔍" text={`${AYLAR_TAM[ay]} ${yil} için kayıt bulunamadı`} />
      )}

      {/* Tablo – Mobil Kartlar */}
      {!loading && liste.length > 0 && (
        <div className="md:hidden space-y-2">
          {liste.map(function(d, idx) {
            var ortGece  = d.girisSayisi > 0 ? (d.gecelemeSayisi / d.girisSayisi).toFixed(1) : '0';
            var girisPct = toplamGiris > 0 ? ((d.girisSayisi / toplamGiris) * 100).toFixed(1) : '0';
            var gecePct  = toplamGeceleme > 0 ? ((d.gecelemeSayisi / toplamGeceleme) * 100).toFixed(1) : '0';
            return (
              <div key={d.ulke} className="bg-white border border-gray-200 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-gray-400 text-xs w-5 text-center flex-shrink-0">{idx + 1}</span>
                  <span className="text-xl flex-shrink-0">{bayrak(d.ulke)}</span>
                  <span className="font-semibold text-gray-800 text-sm flex-1">{d.ulke}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                  <div><span className="text-gray-400">Giriş: </span><strong className="text-blue-600">{d.girisSayisi} kişi</strong></div>
                  <div><span className="text-gray-400">Geceleme: </span><strong className="text-purple-600">{d.gecelemeSayisi} gece</strong></div>
                  <div><span className="text-gray-400">Ort. Gece: </span><strong>{ortGece}</strong></div>
                  <div><span className="text-gray-400">Giriş %: </span><strong>{girisPct}%</strong></div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full" style={{ width: girisPct + '%' }} />
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{gecePct}% gece</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tablo – Masaüstü. Ortak DataTable bileşeni kullanılır. */}
      {!loading && liste.length > 0 && (
        <div className="hidden md:block bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden z-10 relative mt-4">
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-50 bg-gray-50/30">
            <p className="text-sm font-semibold text-gray-700">
              <span className="text-blue-600 font-black">{AYLAR_TAM[ay]} {yil}</span>
              {' — '}Toplam <span className="text-blue-600 font-black">{liste.length}</span> ülke,{' '}
              <span className="text-green-600 font-black">{toplamGiris}</span> misafir
            </p>
          </div>

          <DataTable
            anahtarAlan="ulke"
            satirlar={liste}
            bosIkon="🌍"
            bosMetin="Bu ay için ülke kaydı yok."
            toplamGoster
            kolonlar={[
              {
                anahtar: 'sira', baslik: '#', genislik: 'w-12',
                ciz: (_, i) => <span className="text-xs text-gray-300 font-bold">{i + 1}</span>,
                toplam: () => 'TOPLAM',
              },
              {
                anahtar: 'ulke', baslik: 'Ülke', siralanir: true, filtrelenir: true,
                ciz: d => (
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl font-emoji">{bayrak(d.ulke)}</span>
                    <span className="font-bold text-gray-800">{d.ulke}</span>
                  </div>
                ),
                toplam: () => '',
              },
              {
                anahtar: 'girisSayisi', baslik: 'Giriş (kişi)', hizala: 'orta', siralanir: true,
                ciz: d => <Rozet renk="indigo">{d.girisSayisi}</Rozet>,
                toplam: () => <Rozet renk="indigo">{toplamGiris}</Rozet>,
              },
              {
                anahtar: 'gecelemeSayisi', baslik: 'Geceleme', hizala: 'orta', siralanir: true,
                ciz: d => <Rozet renk="violet">{d.gecelemeSayisi}</Rozet>,
                toplam: () => <Rozet renk="violet">{toplamGeceleme}</Rozet>,
              },
              {
                anahtar: 'ortGeceleme', baslik: 'Ort. geceleme', hizala: 'orta',
                deger: d => (d.girisSayisi > 0 ? d.gecelemeSayisi / d.girisSayisi : 0),
                ciz: d => `${d.girisSayisi > 0 ? (d.gecelemeSayisi / d.girisSayisi).toFixed(1) : '0'} gece`,
                toplam: () => `${toplamGiris > 0 ? (toplamGeceleme / toplamGiris).toFixed(1) : '0'} gece`,
              },
              {
                anahtar: 'girisYuzde', baslik: 'Giriş %', hizala: 'sag',
                deger: d => (toplamGiris > 0 ? (d.girisSayisi / toplamGiris) * 100 : 0),
                ciz: d => <OranCubugu renk="indigo" oran={toplamGiris > 0 ? (d.girisSayisi / toplamGiris) * 100 : 0} />,
                toplam: () => '100%',
              },
              {
                anahtar: 'gecelemeYuzde', baslik: 'Geceleme %', hizala: 'sag',
                deger: d => (toplamGeceleme > 0 ? (d.gecelemeSayisi / toplamGeceleme) * 100 : 0),
                ciz: d => <OranCubugu renk="violet" oran={toplamGeceleme > 0 ? (d.gecelemeSayisi / toplamGeceleme) * 100 : 0} />,
                toplam: () => '100%',
              },
            ]}
          />
        </div>
      )}

      <UsageInfo
        color="blue"
        description="Pazar Payı (Misafir Ülke) İstatistiği, otelinizin daha çok hangi coğrafyalara hitap ettiğini inceleyerek Satış ve Pazarlama alanında nerede efor harcamanız gerektiğini keşfetmenizi sağlar."
        scenarios={[
          { title: "Giriş Yüzdesi ve Pazarlama Bütçesi", content: "Türk veya Alman turistlerin otel doluluğundaki payı %60 ise, otelinizin dijital medya pazarlama bütçesinin büyük bir kısmını İngiltere yerine bu ülkelere ve ilgili dillerdeki Google Reklamlarına (Ads) kaydırabilirsiniz." },
          { title: "Geceleme Tespiti ile Hedefleme", content: "Arap turistlerin sayısı az görünse dahi (%5 giriş), konaklama süreleri çok uzun olduğundan 'Geceleme Yüzdesi' kısmında devasa bir etki (%20 gece) yaratabilirler. Buna göre o ülkeye özel +7 veya +14 gece için 'Uzun Konaklama (Long Stay)' indirim stratejisi kurgulanabilir." },
          { title: "Mutfak ve Animasyon Gelişimi", content: "O ayki demografik patlamalara bakarak restoran menülerine lokal (örneğin artan Rus nüfusu için o coğrafyaya has yöresel çorbalar) eklemeler yapılabilir." }
        ]}
      />
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
